import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import './DeviceView.css';
import DocumentManager from '../../../shared/components/common/DocumentManager';
import ReliabilityMetrics from '../../analytics/components/ReliabilityMetrics';
import DeviceQRCode from './DeviceQRCode';
import { DeviceAuditHistory } from './audit';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';
import { formatCurrency, formatCurrencyDynamic } from '../../../utils/numberUtils';
import { DeviceIDBadge, CriticalityBadge } from '../../../shared/components/common/Badges';
import { AvailabilityImpactBadge } from '../../../shared/components/common/AvailabilityImpactBadge';
import { deviceHasAvailabilityImpact } from '../../../shared/constants/availabilityImpactFilter';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import LocationTransferModal from '../../transfers/components/LocationTransferModal';
import DepartmentTransferModal from '../../department-transfers/components/DepartmentTransferModal';
import TransferActor from '../../transfers/components/TransferActor';
import StatusNotice from '../../../shared/components/common/StatusNotice';
import DeviceIssuesSummary from './DeviceIssuesSummary';
import DeviceSectionNav from './DeviceSectionNav';

function DeviceView({
  deviceId,
  token,
  onEdit,
  onClose,
  user,
  onNavigateBack,
  onViewMaintenanceHistory,
  isInEditResubmitMode = false,
  onNavigateToDashboard = null,
  onNavigateToApprovals = null,
  onNavigateToLocationTransfers = null,
  onNavigateToDepartmentTransfers = null,
  onNavigateToComplaintsWithFilter = null,
  previewInModal = false,
  transferPreviewId = null,
}) {
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandDocuments, setExpandDocuments] = useState(false);
  const [documentCount, setDocumentCount] = useState(0);
  const [technicianAssignments, setTechnicianAssignments] = useState([]);
  const [techLoading, setTechLoading] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferModalMode, setTransferModalMode] = useState('create');
  const [openTransfer, setOpenTransfer] = useState(null);
  const [openDepartmentTransfer, setOpenDepartmentTransfer] = useState(null);
  const [blockingLocationTransfer, setBlockingLocationTransfer] = useState(null);
  const [showDepartmentTransferModal, setShowDepartmentTransferModal] = useState(false);
  const [priorRejections, setPriorRejections] = useState([]);
  const [metricsRefreshKey, setMetricsRefreshKey] = useState(0);
  const [notesExpanded, setNotesExpanded] = useState(false);

  // RESTRICTED: Check if user can edit device (admin and manager only - NOT coordinator)
  const canEdit = user && (user.role === 'admin' || user.role === 'manager');
  // Initiate location transfer: manager and technician only (admin approves on Location Transfers page)
  const canInitiateTransfer = user && ['manager', 'technician'].includes(user.role);
  const canAccessDeviceTransfers =
    user && ['admin', 'manager', 'technician'].includes(user.role);
  const transferNavigate = canAccessDeviceTransfers ? onNavigateToLocationTransfers : null;
  const departmentTransferNavigate =
    user?.role === 'custodian' ? onNavigateToDepartmentTransfers : null;

  // Check if user can see financial data
  const canSeeFinancial = user && ['superadmin', 'admin', 'coordinator', 'manager'].includes(user.role);

  // Check if user can see documents
  const canSeeDocuments = user && ['superadmin', 'admin', 'coordinator', 'manager', 'custodian', 'technician'].includes(user.role);

  // Check if user is admin
  const isAdmin = user && (user.role === 'admin');

  useEffect(() => {
    if (deviceId) {
      setNotesExpanded(false);
      fetchDevice();
      if (!(previewInModal && transferPreviewId)) {
        fetchTechnicianAssignments();
      }
      if (!previewInModal && (canAccessDeviceTransfers || user?.role === 'custodian')) {
        fetchOpenTransfer();
      } else if (!previewInModal) {
        setOpenTransfer(null);
        setOpenDepartmentTransfer(null);
        setBlockingLocationTransfer(null);
      }
      if (!previewInModal) {
        window.scrollTo(0, 0);
      }
    }
  }, [deviceId, previewInModal, transferPreviewId, user?.role]);

  const fetchOpenTransfer = async () => {
    try {
      if (canAccessDeviceTransfers) {
        const response = await axios.get(API_ENDPOINTS.LOCATION_TRANSFERS.OPEN_BY_DEVICE(deviceId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOpenTransfer(response.data?.transfer || null);
      } else {
        setOpenTransfer(null);
      }
    } catch (err) {
      console.error('Error fetching open transfer:', err);
      setOpenTransfer(null);
    }
    try {
      if (user?.role === 'custodian') {
        const response = await axios.get(
          API_ENDPOINTS.DEPARTMENT_TRANSFERS.OPEN_BY_DEVICE(deviceId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOpenDepartmentTransfer(response.data?.transfer || null);
        setBlockingLocationTransfer(response.data?.blocking_location_transfer || null);
      } else {
        setOpenDepartmentTransfer(null);
        setBlockingLocationTransfer(null);
      }
    } catch (err) {
      console.error('Error fetching open department transfer:', err);
      setOpenDepartmentTransfer(null);
      setBlockingLocationTransfer(null);
    }
  };

  const fetchDevice = async (options = {}) => {
    const showLoading = options.showLoading !== false;
    if (showLoading) {
      setLoading(true);
    }
    setError('');
    if (previewInModal && transferPreviewId) {
      setTechLoading(true);
    }

    try {
      const url =
        previewInModal && transferPreviewId
          ? API_ENDPOINTS.LOCATION_TRANSFERS.DEVICE_PREVIEW(transferPreviewId)
          : API_ENDPOINTS.DEVICES.GET(deviceId);
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (previewInModal && transferPreviewId) {
        setDevice(response.data?.device);
        const assignment = response.data?.technician_assignment;
        if (assignment) {
          setTechnicianAssignments([assignment]);
        } else {
          setTechnicianAssignments([]);
        }
        setTechLoading(false);
      } else {
        setDevice(response.data);
      }
    } catch (err) {
      console.error('Error fetching device:', err);
      setError(
        err.response?.data?.error ||
          (previewInModal ? 'Failed to load device preview' : 'Failed to load device details')
      );
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchDevice({ showLoading: false });
    if (!(previewInModal && transferPreviewId)) {
      fetchTechnicianAssignments();
    }
    if (!previewInModal && (canAccessDeviceTransfers || user?.role === 'custodian')) {
      fetchOpenTransfer();
    }
    setMetricsRefreshKey((prev) => prev + 1);
  };

  const fetchTechnicianAssignments = async () => {
    try {
      setTechLoading(true);
      const response = await axios.get(API_ENDPOINTS.DEVICES.TECHNICIANS(deviceId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Handle the response data properly
      // Backend returns either:
      // 1. null/None if no assignment exists
      // 2. Single object with primary_technician and secondary_technician properties
      if (!response.data) {
        // No assignment exists
        setTechnicianAssignments([]);
      } else if (Array.isArray(response.data)) {
        // If response.data is an array, use it directly
        setTechnicianAssignments(response.data);
      } else if (typeof response.data === 'object' && (response.data.primary_technician || response.data.secondary_technician || response.data.device_id)) {
        // Single assignment object - wrap it in an array for consistent handling
        setTechnicianAssignments([response.data]);
      } else {
        // Unknown format
        console.warn('Unexpected response format for technician assignments:', response.data);
        setTechnicianAssignments([]);
      }
    } catch (err) {
      console.error('Error fetching technician assignments:', err);
      setTechnicianAssignments([]);
    } finally {
      setTechLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="device-view-container">
        <div className="loading-state">Loading device details...</div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="device-view-container">
        <div className="error-state">{error || 'Device not found'}</div>
        <button className="action-btn action-secondary" onClick={onNavigateBack || onClose}>
          <span className="action-icon">←</span>
          <span className="action-text">Back</span>
        </button>
      </div>
    );
  }

  const isPendingApproval = device.device_status === 'pending_approval';
  const isInTransfer = device.device_status === 'in_transfer';
  const isReadOnlyDevice = isPendingApproval || isInTransfer;
  const hasOpenTransfer = !!openTransfer;

  const notesText = (device.notes || '').trim();
  const notesPreviewLimit = 180;
  const notesNeedsClamp = notesText.length > notesPreviewLimit;
  const visibleNotes = notesExpanded || !notesNeedsClamp
    ? notesText
    : `${notesText.slice(0, notesPreviewLimit).trimEnd()}…`;
  const hasOpenDepartmentTransfer = !!openDepartmentTransfer;
  const hasBlockingLocationTransfer = !!blockingLocationTransfer;
  const transferringToLocation =
    openTransfer?.to_location_name || device.transferring_to_location_name || null;
  const transferringToDepartment =
    openDepartmentTransfer?.to_department_name ||
    device.transferring_to_department_name ||
    null;
  const isDepartmentTransferLock =
    isInTransfer && Boolean(transferringToDepartment) && !transferringToLocation;
  const transferDestinationName = transferringToLocation || transferringToDepartment || null;
  const isDraftTransfer = openTransfer?.status === 'draft';
  const isAdminDraftNotice = isAdmin && isDraftTransfer;
  const isInitiatorDraftTransfer = canInitiateTransfer && isDraftTransfer;
  const isSubmittedOpenTransfer = hasOpenTransfer && !isDraftTransfer;
  const canInitiateDepartmentTransfer =
    user?.role === 'custodian' &&
    user?.employee_id &&
    device.custodian_employee_id === user.employee_id;
  const isSourceDepartmentTransfer =
    hasOpenDepartmentTransfer &&
    openDepartmentTransfer.requested_by_employee_id === user?.employee_id;
  const isDestDepartmentTransfer =
    hasOpenDepartmentTransfer &&
    openDepartmentTransfer.destination_custodian_employee_id === user?.employee_id;

  const transferStatusTab = (status) => {
    if (status === 'draft') return 'drafts';
    if (status === 'pending_source_approval') return 'action_required';
    return 'in_transit';
  };

  const handleNavigateToLocationTransfers = (nav) => {
    if (transferNavigate) {
      transferNavigate(nav);
    }
  };

  const showReliabilitySection = !isPendingApproval && !isInEditResubmitMode;
  const detailSectionLinks = [
    { id: 'device-section-basic', label: 'Basic' },
    { id: 'device-section-warranty', label: 'Warranty' },
    { id: 'device-section-ppm', label: 'PPM' },
    { id: 'device-section-calibration', label: 'Calibration' },
    { id: 'device-section-compliance', label: 'Compliance' },
    { id: 'device-section-technicians', label: 'Technicians' },
    ...(device.notes ? [{ id: 'device-section-notes', label: 'Notes' }] : []),
    ...(showReliabilitySection
      ? [{ id: 'device-section-reliability', label: 'Reliability' }]
      : []),
    ...(canSeeDocuments ? [{ id: 'device-section-documents', label: 'Documents' }] : []),
  ];

  const scrollToDetailSection = (sectionId) => {
    if (sectionId === 'device-section-documents') {
      setExpandDocuments(true);
    }
    // Allow expand animation / layout to settle before scrolling to documents
    const runScroll = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    if (sectionId === 'device-section-documents') {
      window.setTimeout(runScroll, 50);
    } else {
      runScroll();
    }
  };

  const openTransferModal = (mode) => {
    setTransferModalMode(mode);
    setShowTransferModal(true);
  };

  const handleStartTransfer = async () => {
    setPriorRejections([]);
    try {
      const response = await axios.get(API_ENDPOINTS.LOCATION_TRANSFERS.PRIOR_REJECTIONS(deviceId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPriorRejections(response.data?.rejections || []);
    } catch (err) {
      console.error('Error fetching prior transfer rejections:', err);
      setPriorRejections([]);
    }
    openTransferModal('create');
  };

  const handleTransferModalSuccess = async (opts) => {
    if (opts?.draftSaved) {
      await fetchOpenTransfer();
      return;
    }
    if (opts?.submitted || opts?.completed || opts?.cancelled) {
      setShowTransferModal(false);
      setTransferModalMode('create');
      setPriorRejections([]);
      await fetchOpenTransfer();
      fetchDevice();
      return;
    }
    await fetchOpenTransfer();
    fetchDevice();
    if (opts?.stayOpen) {
      setTransferModalMode('edit-draft');
      return;
    }
  };

  return (
    <div className="device-view-container">
      <div className="device-view-header">
        <div className="header-content">
          <div className="header-title-row">
            <h2>{device.name}</h2>
            <button
              type="button"
              onClick={handleRefresh}
              className="btn-refresh"
              title="Refresh device"
            >
              🔄
            </button>
            {device.device_identifier && (
              <span className="device-id-badge-header">
                <DeviceIDBadge deviceId={device.device_identifier} size="small" />
                <button
                  type="button"
                  className="qr-code-icon-btn"
                  onClick={() => setShowQRCode(true)}
                  title="View QR Code"
                >
                  ◈
                </button>
              </span>
            )}
          </div>
          <div className="header-badges">
            {device.criticality && (
              <CriticalityBadge criticality={device.criticality} size="small" />
            )}
            {deviceHasAvailabilityImpact(device) && (
              <AvailabilityImpactBadge deviceHasImpact size="small" />
            )}
            <span className="header-department">
              <span className="department-icon-large" aria-hidden="true">
                {device.department_icon || '🏢'}
              </span>
              <span className="department-badge-large">
                {device.department_name || device.department || 'N/A'}
              </span>
            </span>
            <span className="location-badge-large" aria-label="Assigned location">
              <span className="location-badge-pin" aria-hidden="true">📍</span>
              <span className="location-badge-text">{device.location_name || 'N/A'}</span>
            </span>
          </div>
        </div>
        <div className="header-actions">
          {(device.created_at || device.updated_at) && (
            <div className="header-metadata" aria-label="Record timestamps">
              {device.created_at && (
                <span className="metadata-item">
                  <span className="metadata-label">Created</span>
                  <span className="metadata-value">
                    {new Date(device.created_at).toLocaleString()}
                  </span>
                </span>
              )}
              {device.created_at && device.updated_at && (
                <span className="metadata-sep" aria-hidden="true">|</span>
              )}
              {device.updated_at && (
                <span className="metadata-item">
                  <span className="metadata-label">Updated</span>
                  <span className="metadata-value">
                    {new Date(device.updated_at).toLocaleString()}
                  </span>
                </span>
              )}
            </div>
          )}
          <div className="action-palette">
            {previewInModal ? (
              <button type="button" className="action-btn action-secondary" onClick={onClose}>
                <span className="action-icon">✕</span>
                <span className="action-text">Close</span>
              </button>
            ) : isPendingApproval ? (
              <>
                {isAdmin ? (
                  <button
                    className="action-btn action-primary"
                    onClick={() => {
                      if (onNavigateToApprovals) {
                        onNavigateToApprovals();
                      }
                    }}
                    title="Go to Device Approvals page to review and approve pending devices"
                  >
                    <span className="action-icon">✓</span>
                    <span className="action-text">Go to Device Approvals</span>
                  </button>
                ) : (
                  <button
                    className="action-btn action-secondary"
                    onClick={() => {
                      if (onNavigateToDashboard) {
                        onNavigateToDashboard();
                      }
                    }}
                  >
                    <span className="action-icon">🏠</span>
                    <span className="action-text">Go to Dashboard</span>
                  </button>
                )}
              </>
            ) : isInTransfer ? (
              <>
                {transferNavigate && openTransfer && (
                  <button className="action-btn action-primary" type="button" onClick={transferNavigate}>
                    <span className="action-icon">🚚</span>
                    <span className="action-text">Location Transfers</span>
                  </button>
                )}
                {departmentTransferNavigate && openDepartmentTransfer && (
                  <button
                    className="action-btn action-primary"
                    type="button"
                    onClick={() =>
                      departmentTransferNavigate({
                        direction: isDestDepartmentTransfer ? 'incoming' : 'outgoing',
                        statusTab: 'action_required',
                      })
                    }
                  >
                    <span className="action-icon">🔁</span>
                    <span className="action-text">Department Transfers</span>
                  </button>
                )}
                <button className="action-btn action-secondary" onClick={onNavigateBack || onClose}>
                  <span className="action-icon">←</span>
                  <span className="action-text">Back</span>
                </button>
              </>
            ) : (
              <>
                {/* View Maintenance History - Hidden in edit-resubmit mode */}
                {onViewMaintenanceHistory && !isInEditResubmitMode && (
                  <button className="action-btn action-secondary" onClick={() => onViewMaintenanceHistory({ device_identifier: device.device_identifier })} title="View maintenance history and complaints for this device">
                    <span className="action-icon">⚙️</span>
                    <span className="action-text">Maintenance History</span>
                  </button>
                )}
                {canInitiateTransfer && device.device_status === 'active' && !hasOpenTransfer && !hasOpenDepartmentTransfer && (
                  <button
                    className="action-btn action-secondary"
                    type="button"
                    onClick={handleStartTransfer}
                    title="Transfer device to another location"
                  >
                    <span className="action-icon">🚚</span>
                    <span className="action-text">Transfer Location</span>
                  </button>
                )}
                {canInitiateDepartmentTransfer &&
                  device.device_status === 'active' &&
                  !hasOpenTransfer &&
                  !hasOpenDepartmentTransfer &&
                  !hasBlockingLocationTransfer && (
                  <button
                    className="action-btn action-secondary"
                    type="button"
                    onClick={() => setShowDepartmentTransferModal(true)}
                    title="Transfer device to another department at this location"
                  >
                    <span className="action-icon">🔁</span>
                    <span className="action-text">Transfer Department</span>
                  </button>
                )}
                {canInitiateTransfer && isDraftTransfer && (
                  <button
                    className="action-btn action-primary"
                    type="button"
                    onClick={() => openTransferModal('edit-draft')}
                    title="Continue editing the draft transfer"
                  >
                    <span className="action-icon">📝</span>
                    <span className="action-text">Continue draft</span>
                  </button>
                )}
                {canInitiateTransfer && isSubmittedOpenTransfer && transferNavigate && (
                  <button
                    className="action-btn action-primary"
                    type="button"
                    onClick={() =>
                      handleNavigateToLocationTransfers({
                        direction: 'outgoing',
                        statusTab: transferStatusTab(openTransfer.status),
                      })
                    }
                    title="View transfer in Location Transfers"
                  >
                    <span className="action-icon">🚚</span>
                    <span className="action-text">View transfer</span>
                  </button>
                )}
                {canEdit && onEdit && !isInTransfer && !isSubmittedOpenTransfer && !hasOpenDepartmentTransfer && (
                  <button className="action-btn action-primary" onClick={() => onEdit(device)}>
                    <span className="action-icon">✏️</span>
                    <span className="action-text">Edit Device</span>
                  </button>
                )}
                <button className="action-btn action-secondary" onClick={onNavigateBack || onClose}>
                  <span className="action-icon">←</span>
                  <span className="action-text">Back</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {!previewInModal && isAdminDraftNotice && (
        <StatusNotice
          variant="accent"
          title="Transfer draft in progress"
          meta={
            openTransfer.request_reason
              ? `Draft reason: ${openTransfer.request_reason}`
              : null
          }
        >
          <p>
            The following user is preparing a transfer of this device to{' '}
            <strong>{openTransfer.to_location_name || 'another location'}</strong>. The device stays
            active and editable until they submit for approval. You can review and approve in{' '}
            <strong>Location Transfers → Action required</strong> once submitted.
          </p>
          <TransferActor
            name={openTransfer.requested_by_name || openTransfer.requested_by_employee_id}
            role={openTransfer.requested_by_role}
          />
        </StatusNotice>
      )}

      {!previewInModal && isInitiatorDraftTransfer && (
        <StatusNotice
          variant="warning"
          icon="✎"
          title="Transfer draft in progress"
          actions={
            <>
              <button
                type="button"
                className="action-btn action-primary"
                onClick={() => openTransferModal('edit-draft')}
              >
                <span className="action-text">Continue draft</span>
              </button>
              {transferNavigate && (
                <button
                  type="button"
                  className="action-btn action-secondary"
                  onClick={() => handleNavigateToLocationTransfers({ direction: 'outgoing', statusTab: 'drafts' })}
                >
                  <span className="action-text">Go to Drafts</span>
                </button>
              )}
            </>
          }
        >
          A transfer to <strong>{openTransfer.to_location_name || 'another location'}</strong> is
          saved as a draft. Use <strong>Continue draft</strong> here, or open{' '}
          <strong>Location Transfers → Outgoing → Drafts</strong> to edit and submit for approval.
          The device stays editable until you submit.
        </StatusNotice>
      )}

      {!previewInModal &&
        canInitiateDepartmentTransfer &&
        hasBlockingLocationTransfer &&
        !hasOpenDepartmentTransfer &&
        !isInTransfer && (
        <StatusNotice
          variant="warning"
          title={
            blockingLocationTransfer.status === 'draft'
              ? 'Transfer draft in progress'
              : 'Location transfer in progress'
          }
          meta={
            blockingLocationTransfer.request_reason
              ? `Draft reason: ${blockingLocationTransfer.request_reason}`
              : null
          }
        >
          <p>
            {blockingLocationTransfer.status === 'draft' ? (
              <>
                The following user is preparing a location transfer of this device to{' '}
                <strong>{blockingLocationTransfer.to_location_name || 'another location'}</strong>.
                {' '}Department transfer is unavailable until this draft is discarded.
              </>
            ) : (
              <>
                A location transfer to{' '}
                <strong>{blockingLocationTransfer.to_location_name || 'another location'}</strong>
                {' '}is in progress. Department transfer is unavailable until that transfer completes
                or is cancelled.
              </>
            )}
          </p>
          {(blockingLocationTransfer.requested_by_name ||
            blockingLocationTransfer.requested_by_employee_id) && (
            <TransferActor
              name={
                blockingLocationTransfer.requested_by_name ||
                blockingLocationTransfer.requested_by_employee_id
              }
              role={blockingLocationTransfer.requested_by_role}
            />
          )}
        </StatusNotice>
      )}

      {!previewInModal && isInTransfer && (
        <StatusNotice
          variant="info"
          icon={isDepartmentTransferLock ? '🔁' : '🚚'}
          plainIcon
          title={
            transferDestinationName
              ? `Transferring to ${transferDestinationName}`
              : 'Device transferring'
          }
        >
          {transferDestinationName
            ? `This device is locked while transferring to ${transferDestinationName}. Editing, complaints, and maintenance are disabled until the transfer completes or is cancelled.`
            : 'This device is locked while a transfer is in progress. Editing, complaints, and maintenance are disabled until the transfer completes or is cancelled.'}
        </StatusNotice>
      )}

      {isPendingApproval && (
        <StatusNotice
          variant="warning"
          icon="…"
          title={isAdmin ? 'Device Awaiting Your Approval' : 'Device Pending Admin Approval'}
        >
          {isAdmin ? (
            <>
              <p>
                This device has been submitted for your review and approval. Please review the device
                details and approve or request changes as needed.
              </p>
              <p>
                Visit the <strong>Device Approvals</strong> page to review pending devices and take
                action.
              </p>
            </>
          ) : (
            <>
              <p>
                This device has been submitted for admin approval and cannot be edited until the
                approval is completed.
              </p>
              <p>
                An admin will review this device shortly. Please check your dashboard for approval
                status updates.
              </p>
            </>
          )}
        </StatusNotice>
      )}

      {/* Tab Navigation - Hidden for pending approval and transfer preview modal */}
      {!previewInModal && !isPendingApproval && (
        <div className="device-tabs" role="tablist" aria-label="Device sections">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'details'}
              className={`device-tab-btn ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Device details
            </button>
            {user && ['admin', 'superadmin'].includes(user.role) && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'audit'}
                className={`device-tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
                onClick={() => setActiveTab('audit')}
              >
                Audit history
              </button>
            )}
        </div>
      )}

      {/* Issue summary — compact chips; details on demand */}
      {activeTab === 'details' && (
        <DeviceIssuesSummary
          device={device}
          user={user}
          onNavigateToComplaintsWithFilter={onNavigateToComplaintsWithFilter}
          hidden={isPendingApproval}
        />
      )}

      {/* Device Details Content - Only visible when details tab is active */}
      {activeTab === 'details' && (
        <>
          <DeviceSectionNav
            links={detailSectionLinks}
            onNavigate={scrollToDetailSection}
            inModal={previewInModal}
          />

          <div className="device-view-content">
            {/* Basic Device Information */}
            <div className="info-card" id="device-section-basic">
              <h3>Basic Device Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Device Name</span>
                  <span className="info-value">{device.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Model</span>
                  <span className="info-value">{device.model}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Serial Number</span>
                  <span className="info-value">{device.serial_number}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Year of Manufacture</span>
                  <span className="info-value">{device.year_of_manufacture || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Manufacturer</span>
                  <span className="info-value">{device.manufacturer}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Supplier</span>
                  <span className="info-value">{device.supplier || 'Not set'}</span>
                </div>
                {canSeeFinancial && (
                  <div className="info-item">
                    <span className="info-label">Product Cost (₹)</span>
                    <span className="info-value">{formatCurrency(device.product_cost)} 
                      <span style={{ fontWeight: 'bold', marginLeft: '0.5rem' }}>
                        ({formatCurrencyDynamic(device.product_cost)})
                      </span>
                    </span>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">Department</span>
                  <span className="info-value">
                    {device.department_name || device.department || 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Custodian Name</span>
                  <span className="info-value">{device.custodian_name || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Physical Location</span>
                  <span className="info-value">{device.physical_location}</span>
                </div>
              </div>
            </div>

            {/* Warranty & Service Contract */}
            <div className="info-card" id="device-section-warranty">
              <h3>Warranty & Service Contract</h3>
              <div className="info-grid">
                {/* Installation Date - always visible */}
                <div className="info-item">
                  <span className="info-label">Installation Date</span>
                  <span className="info-value">{formatDate(device.installation_date)}</span>
                </div>

                {/* Service Contract Type - always visible */}
                <div className="info-item">
                  <span className="info-label">Service Contract Type</span>
                  <span className="info-value">{device.service_contract_type || 'Not set'}</span>
                </div>

                {/* Warranty Fields - show only when "Warranty" is selected */}
                {device.service_contract_type === 'Warranty' && (
                  <>
                    <div className="info-item">
                      <span className="info-label">Warranty Period (Years)</span>
                      <span className="info-value">{device.warranty_period ? `${device.warranty_period} year${device.warranty_period !== '1' ? 's' : ''}` : 'Not set'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Warranty Expiry Date</span>
                      <span className="info-value">{formatDate(device.warranty_expiry)}</span>
                    </div>
                  </>
                )}

                {/* Service Contract Fields - show only when "LAMC" or "CAMC" is selected */}
                {(device.service_contract_type === 'LAMC' || device.service_contract_type === 'CAMC') && (
                  <>
                    <div className="info-item">
                      <span className="info-label">Service Contract Period (Years)</span>
                      <span className="info-value">{device.service_contract_period ? `${device.service_contract_period} year${device.service_contract_period !== '1' ? 's' : ''}` : 'Not set'}</span>
                    </div>
                    {canSeeFinancial && (
                      <div className="info-item">
                        <span className="info-label">Service Contract Cost (₹)</span>
                        <span className="info-value">{formatCurrency(device.service_contract_cost)}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <span className="info-label">Contract Start Date</span>
                      <span className="info-value">{formatDate(device.contract_start_date)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Contract Expiry Date</span>
                      <span className="info-value">{formatDate(device.contract_expiry)}</span>
                    </div>
                  </>
                )}

                {/* In-house contract - show only when "In-house" is selected */}
                {device.service_contract_type === 'In-house' && (
                  <div className="info-item">
                    <span className="info-label">Service Type</span>
                    <span className="info-value">In-house Maintenance (No external contract costs)</span>
                  </div>
                )}
              </div>
            </div>

            {/* PPM (Preventive Maintenance) */}
            <div className="info-card" id="device-section-ppm">
              <h3>PPM (Preventive Maintenance)</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">PPM Frequency</span>
                  <span className="info-value">{device.ppm_frequency || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">PPM Last Done Date</span>
                  <span className="info-value">{formatDate(device.ppm_last_done_date)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">PPM Next Due Date</span>
                  <span className="info-value">{formatDate(device.ppm_next_due_date)}</span>
                </div>
              </div>
            </div>

            {/* Calibration */}
            <div className="info-card" id="device-section-calibration">
              <h3>Calibration / Validation</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Calibration Required</span>
                  <span className="info-value">{device.calibration_required || 'Not set'}</span>
                </div>
                {device.calibration_required === 'Yes' && (
                  <>
                    <div className="info-item">
                      <span className="info-label">Calibration Frequency</span>
                      <span className="info-value">{device.calibration_frequency || 'Not set'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Calibration Last Done Date</span>
                      <span className="info-value">{formatDate(device.calibration_last_done_date)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Calibration Next Due Date</span>
                      <span className="info-value">{formatDate(device.calibration_next_due_date)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Regulatory Compliance */}
            <div className="info-card" id="device-section-compliance">
              <h3>Regulatory Compliance</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Compliance Required</span>
                  <span className="info-value">{device.compliance_required || 'Not set'}</span>
                </div>
                {device.compliance_required === 'Yes' && (
                  <>
                    <div className="info-item">
                      <span className="info-label">Compliance Type</span>
                      <span className="info-value compliance-type-badge">{device.compliance_type || 'Not set'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Compliance Frequency</span>
                      <span className="info-value">{device.compliance_frequency || 'Not set'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Compliance Last Done Date</span>
                      <span className="info-value">{formatDate(device.compliance_last_done_date)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Compliance Next Due Date</span>
                      <span className="info-value">{formatDate(device.compliance_next_due_date)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Technician Assignment */}
            <div className="info-card" id="device-section-technicians">
              <h3>🔧 Technician Assignment</h3>
              <p className="tech-assign-intro">
                Technicians assigned for all maintenance activities (PPM, Calibration, Regulatory Compliance) and complaints for this device
              </p>
              {techLoading ? (
                <div className="tech-assign-loading">Loading technician assignments...</div>
              ) : technicianAssignments.length === 0 ? (
                <div className="tech-assign-empty-state">
                  {previewInModal
                    ? 'No technicians were assigned at the source location for this device.'
                    : 'No technicians assigned yet. Click "Edit Device" to assign technicians.'}
                </div>
              ) : (
                <div className="tech-assign-panel">
                  <div className="tech-assign-grid">
                    <div className="tech-assign-slot">
                      <div className="tech-assign-label">Primary Technician</div>
                      {technicianAssignments[0]?.primary_technician ? (
                        <div className="tech-assign-card">
                          <div className="tech-assign-name">{technicianAssignments[0].primary_technician.full_name}</div>
                          <div className="tech-assign-detail">
                            <span className="tech-assign-icon" aria-hidden="true">✉️</span>
                            <span className="tech-assign-detail-text">{technicianAssignments[0].primary_technician.email}</span>
                          </div>
                          <div className="tech-assign-detail">
                            <span className="tech-assign-icon" aria-hidden="true">📞</span>
                            <span className="tech-assign-detail-text">
                              {technicianAssignments[0].primary_technician.contact_number
                                ? formatPhoneNumber(technicianAssignments[0].primary_technician.contact_number)
                                : 'Not available'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="tech-assign-card tech-assign-card--empty">
                          <span>ℹ️</span>
                          Not assigned
                        </div>
                      )}
                    </div>
                    <div className="tech-assign-slot">
                      <div className="tech-assign-label">Secondary Technician (Optional)</div>
                      {technicianAssignments[0]?.secondary_technician ? (
                        <div className="tech-assign-card">
                          <div className="tech-assign-name">{technicianAssignments[0].secondary_technician.full_name}</div>
                          <div className="tech-assign-detail">
                            <span className="tech-assign-icon" aria-hidden="true">✉️</span>
                            <span className="tech-assign-detail-text">{technicianAssignments[0].secondary_technician.email}</span>
                          </div>
                          <div className="tech-assign-detail">
                            <span className="tech-assign-icon" aria-hidden="true">📞</span>
                            <span className="tech-assign-detail-text">
                              {technicianAssignments[0].secondary_technician.contact_number
                                ? formatPhoneNumber(technicianAssignments[0].secondary_technician.contact_number)
                                : 'Not available'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="tech-assign-card tech-assign-card--empty">
                          <span>ℹ️</span>
                          Not assigned
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            {notesText && (
              <div className="info-card" id="device-section-notes">
                <h3>Additional Notes</h3>
                <p className={`notes-content${notesExpanded ? ' is-expanded' : ''}`}>
                  {visibleNotes}
                </p>
                {notesNeedsClamp && (
                  <button
                    type="button"
                    className="notes-content-toggle"
                    onClick={() => setNotesExpanded((prev) => !prev)}
                  >
                    {notesExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )}
      </div>

      {/* Reliability Metrics - Hidden for pending approval and edit-resubmit mode */}
      {showReliabilitySection && (
          <div className="device-view-content" id="device-section-reliability">
            <ReliabilityMetrics
              deviceId={device.id}
              token={token}
              user={user}
              readOnlyReviewMode={previewInModal}
              refreshKey={metricsRefreshKey}
            />
          </div>
      )}

      {/* Documents & Attachments - Always visible (even for pending approval), but read-only */}
      {canSeeDocuments && (
        <div className="device-view-content" id="device-section-documents">
          <div className="documents-section">
            <div className="section-header collapsible-header" onClick={() => setExpandDocuments(!expandDocuments)}>
              <div className="header-title-wrapper">
                <span className="collapse-icon">{expandDocuments ? '▼' : '▶'}</span>
                <div className="header-content">
                  <h4>📎 Documents & Attachments</h4>
                </div>
              </div>
              <p className="section-description">Device documentation and related files</p>
              <span className="complaint-count-badge">{documentCount}</span>
            </div>

            {expandDocuments && (
              <div className="documents-content expand-animation">
                <DocumentManager
                  deviceId={device.id}
                  token={token}
                  onDocumentCountChange={setDocumentCount}
                  user={user}
                  deviceStatus={device.device_status}
                  isSubmittedForApproval={isReadOnlyDevice}
                />
              </div>
            )}

            {/* Always render hidden DocumentManager to keep count updated */}
            {!expandDocuments && (
              <div style={{ display: 'none' }}>
                <DocumentManager
                  deviceId={device.id}
                  token={token}
                  onDocumentCountChange={setDocumentCount}
                  user={user}
                  deviceStatus={device.device_status}
                  isSubmittedForApproval={isReadOnlyDevice}
                />
              </div>
            )}
          </div>
        </div>
      )}

        </>
      )}

      {/* Audit History Tab Content - Only visible when audit tab is active */}
      {activeTab === 'audit' && !isPendingApproval && !isInEditResubmitMode && (
        <DeviceAuditHistory deviceId={deviceId} token={token} user={user} />
      )}

      {/* QR Code Display Modal - Using ReactDOM.createPortal for consistency with DeviceList */}
      {showQRCode && device && ReactDOM.createPortal(
        <DeviceQRCode
          device={device}
          onClose={() => setShowQRCode(false)}
        />,
        document.body
      )}

      {!previewInModal && canInitiateTransfer && showTransferModal && device && (
        <LocationTransferModal
          mode={transferModalMode}
          device={device}
          transfer={transferModalMode === 'edit-draft' ? openTransfer : null}
          token={token}
          user={user}
          priorRejections={transferModalMode === 'create' ? priorRejections : []}
          onClose={() => {
            setShowTransferModal(false);
            setTransferModalMode('create');
            setPriorRejections([]);
          }}
          onSuccess={handleTransferModalSuccess}
          onNavigateToLocationTransfers={handleNavigateToLocationTransfers}
        />
      )}

      {!previewInModal && showDepartmentTransferModal && device && (
        <DepartmentTransferModal
          mode="create"
          device={device}
          token={token}
          user={user}
          onClose={() => setShowDepartmentTransferModal(false)}
          onSuccess={async () => {
            setShowDepartmentTransferModal(false);
            await fetchDevice();
            await fetchOpenTransfer();
          }}
        />
      )}
    </div>
  );
}

export default DeviceView;
